import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@frontend/contexts/AuthContext'
import { usePermissions } from '@frontend/hooks/usePermissions'
import { Button } from '@frontend/components/ui/button'
import { LogOut, FileCheck, Users, Shield, Building2, Signature, ShieldAlert } from 'lucide-react'

interface NavItem {
  label: string
  href: string
  icon: React.ReactNode
}

const navItems: NavItem[] = [
  {
    label: 'Vendors',
    href: '/vendors',
    icon: <Building2 className="h-4 w-4" />,
  },
  {
    label: 'Compliance Reviews',
    href: '/reviews',
    icon: <FileCheck className="h-4 w-4" />,
  },
  {
    label: 'Risks',
    href: '/risks',
    icon: <ShieldAlert className="h-4 w-4" />,
  },
  {
    label: 'Auditors',
    href: '/auditors',
    icon: <Signature className="h-4 w-4" />,
  },
  {
    label: 'Clients',
    href: '/clients',
    icon: <Users className="h-4 w-4" />,
  },
]

interface AppLayoutProps {
  children: React.ReactNode
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, logout } = useAuth()
  const { can } = usePermissions()
  const location = useLocation()

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/50 bg-card/80 backdrop-blur-sm">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo & Nav */}
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2">
              <img src="/assets/logo.png" alt="EdenData" className="h-8 w-8 rounded-lg" />
              <span className="text-lg font-semibold tracking-tight">Garden</span>
            </Link>

            {/* Navigation */}
            <nav className="hidden items-center gap-1 md:flex">
              {navItems.map((item) => {
                const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/')
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-foreground/5 text-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                )
              })}
              {can('users.view') && (
                <Link
                  to="/users"
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    location.pathname === '/users' || location.pathname.startsWith('/users/')
                      ? 'bg-foreground/5 text-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Shield className="h-4 w-4" />
                  User Management
                </Link>
              )}
            </nav>
          </div>

          {/* User menu */}
          <div className="flex items-center gap-3">
            <div className="hidden text-sm sm:block">
              <span className="text-muted-foreground">{user?.email}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="text-muted-foreground hover:text-foreground">
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">{children}</main>
    </div>
  )
}
